import type { LucideIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { normalizeReferenceLabel, ReferenceChip } from "@/components/ui/reference-chip";
import {
  buildEvidenceReferenceKey,
  DEFAULT_DOCUMENT_EVIDENCE_LABEL,
  type DocumentEvidenceCitation,
  evidenceDocumentIcon,
  useDocumentEvidenceOwner,
  useDocumentEvidencePresentations,
  useOptionalDocumentEvidenceInteraction,
} from "@/features/rich-text/evidence";
import { classifyDocumentEvidenceHref } from "@/lib/document-evidence-link";
import { flattenReactNodeText } from "@/lib/react-node-text";

/** Shown while a run has nothing available yet, matching the entity-content wording. */
const UNAVAILABLE_EVIDENCE_TEXT = "Evidence unavailable";

/**
 * The hast property the run transform stamps on the head link of a run. Rendering reads it
 * back off the node react-markdown hands to the `a` component, so the run never has to
 * survive a round trip through DOM attributes.
 */
const EVIDENCE_RUN_PROPERTY = "dataEvidenceRun";

/** One evidence link of a run, as the transform serializes it for the renderer. */
export interface DocumentEvidenceRunLink {
  href: string;
  label: string;
}

/**
 * The slice of hast this transform needs. Typed structurally rather than through `hast`:
 * `@types/hast` is a transitive dependency of react-markdown, not one this app declares.
 */
interface HastNodeLike {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown> | null;
  children?: HastNodeLike[];
}

function isWhitespaceOnlyText(node: HastNodeLike): boolean {
  return node.type === "text" && (node.value ?? "").trim() === "";
}

function isLineBreak(node: HastNodeLike): boolean {
  return node.type === "element" && node.tagName === "br";
}

/**
 * What may sit between two citations without ending their run: authoring noise. A hard
 * break counts, because "sentence.  \n[cite][cite]" is one citation group written across
 * two lines, not two groups.
 */
function isRunSeparator(node: HastNodeLike): boolean {
  return isWhitespaceOnlyText(node) || isLineBreak(node);
}

function readNodeText(node: HastNodeLike): string {
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(readNodeText).join("");
}

/**
 * A link that is a usable citation. A reserved-but-invalid evidence link is deliberately
 * not one: it renders as inert text, so it is "something between" two citations and breaks
 * the run rather than joining it.
 */
function readEvidenceLink(node: HastNodeLike): DocumentEvidenceRunLink | null {
  if (node.type !== "element" || node.tagName !== "a") return null;
  const href = node.properties?.href;
  if (typeof href !== "string") return null;
  if (classifyDocumentEvidenceHref(href).kind !== "valid") return null;
  return { href, label: readNodeText(node).trim() };
}

function stampRun(head: HastNodeLike, links: readonly DocumentEvidenceRunLink[]): void {
  const properties = head.properties ?? {};
  properties[EVIDENCE_RUN_PROPERTY] = JSON.stringify(links);
  head.properties = properties;
}

function groupEvidenceRunsInNode(node: HastNodeLike): void {
  const children = node.children;
  if (!Array.isArray(children)) return;

  const kept: HastNodeLike[] = [];
  let head: HastNodeLike | null = null;
  let links: DocumentEvidenceRunLink[] = [];
  // Whitespace (or a hard break) between two citations is authoring noise that disappears
  // with the follower it separated; a separator that turns out to end the run is ordinary
  // content and is kept.
  let separators: HastNodeLike[] = [];

  const closeRun = () => {
    if (head) stampRun(head, links);
    head = null;
    links = [];
    kept.push(...separators);
    separators = [];
  };

  for (const child of children) {
    const link = readEvidenceLink(child);
    if (link) {
      if (head) {
        // A follower of the run: its citation moves to the head marker and the link node
        // (plus the whitespace that separated it) leaves the tree.
        links.push(link);
        separators = [];
        continue;
      }
      head = child;
      links = [link];
      separators = [];
      kept.push(child);
      continue;
    }

    if (head && isRunSeparator(child)) {
      separators.push(child);
      continue;
    }

    closeRun();
    kept.push(child);
  }
  closeRun();

  node.children = kept;
  for (const child of kept) groupEvidenceRunsInNode(child);
}

/** Elements that carry blocks rather than a line of text, so they are never a host. */
const BLOCK_TAG_NAMES = new Set([
  "p",
  "ul",
  "ol",
  "li",
  "dl",
  "dd",
  "dt",
  "div",
  "section",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "pre",
  "table",
  "hr",
]);

/**
 * The stamped run heads of a paragraph that holds citations and nothing else (whitespace
 * between them is authoring noise), or `null` when the paragraph has any other content.
 * Post-grouping such a paragraph holds exactly one head; the list keeps the caller honest
 * rather than assuming it.
 */
function readEvidenceOnlyParagraph(node: HastNodeLike): HastNodeLike[] | null {
  if (node.type !== "element" || node.tagName !== "p") return null;

  const heads: HastNodeLike[] = [];
  for (const child of node.children ?? []) {
    if (isWhitespaceOnlyText(child)) continue;
    if (!readDocumentEvidenceRunLinks(child)) return null;
    heads.push(child);
  }
  return heads.length > 0 ? heads : null;
}

/**
 * `skip` means "nothing here, keep looking further back"; `rejected` means the nearest
 * thing that *would* have hosted refuses to, so the citations stay where they are.
 */
type HostResolution =
  | { status: "host"; host: HastNodeLike }
  | { status: "skip" }
  | { status: "rejected" };

const SKIP_HOST: HostResolution = { status: "skip" };
const REJECT_HOST: HostResolution = { status: "rejected" };

function resolveHostInChildren(children: readonly HastNodeLike[]): HostResolution {
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const child = children[index];
    if (!child) continue;
    const resolved = resolveHostInNode(child);
    if (resolved.status !== "skip") return resolved;
  }
  return SKIP_HOST;
}

/**
 * A list item is a line of text when it holds inline content (a tight list) and a stack of
 * blocks when it does not (a loose one) — so the marker lands on the item itself in the
 * first case and inside its last block in the second.
 */
function resolveHostInListItem(item: HastNodeLike): HostResolution {
  const children = item.children ?? [];
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const child = children[index];
    if (!child || isWhitespaceOnlyText(child)) continue;
    if (child.type === "element" && BLOCK_TAG_NAMES.has(child.tagName ?? "")) {
      const resolved = resolveHostInNode(child);
      if (resolved.status !== "skip") return resolved;
      continue;
    }
    return { status: "host", host: item };
  }
  return SKIP_HOST;
}

function resolveHostInNode(node: HastNodeLike): HostResolution {
  if (isWhitespaceOnlyText(node)) return SKIP_HOST;
  if (node.type !== "element") return REJECT_HOST;

  const tagName = node.tagName ?? "";
  if (tagName === "p") {
    // Two citation paragraphs in a row belong to the same line of text, so the first one is
    // skipped rather than used as a host — both end up on the same line.
    return readEvidenceOnlyParagraph(node) ? SKIP_HOST : { status: "host", host: node };
  }
  if (tagName === "li") return resolveHostInListItem(node);
  // A citation paragraph after a list belongs to that list's last line, so descend.
  if (tagName === "ul" || tagName === "ol" || tagName === "blockquote") {
    return resolveHostInChildren(node.children ?? []);
  }
  // A heading titles what follows it, and a code block, table or rule has no line of prose
  // to append to: the citations stay visible where the model put them.
  return REJECT_HOST;
}

function findHoistHost(precedingSiblings: readonly HastNodeLike[]): HastNodeLike | null {
  const resolved = resolveHostInChildren(precedingSiblings);
  return resolved.status === "host" ? resolved.host : null;
}

/**
 * Index of the last child that is neither trailing whitespace nor a stamped run, plus the
 * run it stopped at when the host already ends in one.
 */
function readHostTail(host: HastNodeLike): {
  lastIndex: number;
  trailingRun: HastNodeLike | null;
} {
  const children = host.children ?? [];
  let index = children.length - 1;
  while (index >= 0 && isWhitespaceOnlyText(children[index] as HastNodeLike)) index -= 1;
  const last = index >= 0 ? children[index] : undefined;
  return {
    lastIndex: index,
    trailingRun: last && readDocumentEvidenceRunLinks(last) ? last : null,
  };
}

function endsWithWhitespace(node: HastNodeLike | undefined): boolean {
  return node?.type === "text" ? /\s$/.test(node.value ?? "") : false;
}

/**
 * Moves a citation-only paragraph's run to the end of `host`. A host that already ends in a
 * marker absorbs the run instead of growing a second one next to it: adjacent citations are
 * one marker wherever the adjacency came from.
 */
function appendEvidenceRunToHost(host: HastNodeLike, heads: readonly HastNodeLike[]): void {
  const links = heads.flatMap((head) => readDocumentEvidenceRunLinks(head) ?? []);
  if (links.length === 0) return;

  const children = host.children ?? [];
  const { lastIndex, trailingRun } = readHostTail(host);
  if (trailingRun) {
    stampRun(trailingRun, [...(readDocumentEvidenceRunLinks(trailingRun) ?? []), ...links]);
    return;
  }

  const head = heads[0];
  if (!head) return;
  stampRun(head, links);
  // The marker follows the sentence it supports, so it needs the space the paragraph break
  // used to provide — unless the host already ends in one.
  const needsSpace =
    lastIndex === children.length - 1 && lastIndex >= 0 && !endsWithWhitespace(children[lastIndex]);
  if (needsSpace) children.push({ type: "text", value: " " });
  children.push(head);
  host.children = children;
}

/**
 * Drops the hard break in "sentence.  \n[cite]": the citations that close a paragraph belong
 * on the line they support, not on one of their own. A break with real prose after it is
 * untouched.
 */
function dropTrailingBreakBeforeEvidence(node: HastNodeLike): void {
  const children = node.children;
  if (!Array.isArray(children) || children.length === 0) return;

  let index = children.length - 1;
  let sawEvidence = false;
  while (index >= 0) {
    const child = children[index] as HastNodeLike;
    if (isWhitespaceOnlyText(child)) {
      index -= 1;
      continue;
    }
    if (!readDocumentEvidenceRunLinks(child)) break;
    sawEvidence = true;
    index -= 1;
  }
  if (!sawEvidence) return;

  let removed = false;
  while (index >= 0) {
    const child = children[index] as HastNodeLike;
    if (isLineBreak(child) || (removed && isWhitespaceOnlyText(child))) {
      children.splice(index, 1);
      removed = removed || isLineBreak(child);
      index -= 1;
      continue;
    }
    break;
  }
  if (!removed) return;

  const previous = index >= 0 ? children[index] : undefined;
  if (previous && !endsWithWhitespace(previous)) {
    children.splice(index + 1, 0, { type: "text", value: " " });
  }
}

function hoistEvidenceRunsInNode(node: HastNodeLike): void {
  const children = node.children;
  if (!Array.isArray(children)) return;

  // Depth first: a list item hoists its own citation paragraph before the list is offered as
  // a host, so a citation after the list merges into the marker already on its last line.
  for (const child of children) hoistEvidenceRunsInNode(child);
  dropTrailingBreakBeforeEvidence(node);

  const kept: HastNodeLike[] = [];
  let hoisted = false;
  for (const child of children) {
    const heads = readEvidenceOnlyParagraph(child);
    if (heads) {
      const host = findHoistHost(kept);
      if (host) {
        appendEvidenceRunToHost(host, heads);
        hoisted = true;
        continue;
      }
    }
    kept.push(child);
  }
  if (hoisted) node.children = kept;
}

/**
 * Collapses runs of adjacent evidence links — nothing but whitespace or a hard break between
 * them, inside one block — into their first link, which carries the whole run's citations.
 * Every valid evidence link is stamped, runs of one included, so the renderer has a single
 * path. Grouping happens per parent, so a run can never span a block boundary; non-evidence
 * links are untouched.
 *
 * Then the citations Claudia writes on a line of their own are *hoisted*: a paragraph holding
 * nothing but a run moves to the end of the preceding line of text (the previous paragraph,
 * the last bullet of the preceding list, the last line of a quote), mirroring what entity
 * content does with the same shape. Nothing hosts a citation for text it does not contain, so
 * a heading, a code block, a table or a rule leaves the paragraph exactly where it is.
 *
 * Order matters: grouping first, so hoisting sees one stamped head per line rather than a
 * string of links.
 */
export function rehypeDocumentEvidenceRuns() {
  return (tree: unknown) => {
    if (!tree || typeof tree !== "object") return;
    groupEvidenceRunsInNode(tree as HastNodeLike);
    hoistEvidenceRunsInNode(tree as HastNodeLike);
  };
}

/** The run stamped on `node` by {@link rehypeDocumentEvidenceRuns}, or `null`. */
export function readDocumentEvidenceRunLinks(node: unknown): DocumentEvidenceRunLink[] | null {
  if (!node || typeof node !== "object") return null;
  const serialized = (node as HastNodeLike).properties?.[EVIDENCE_RUN_PROPERTY];
  if (typeof serialized !== "string") return null;

  try {
    const parsed: unknown = JSON.parse(serialized);
    if (!Array.isArray(parsed)) return null;
    const links = parsed.filter(
      (entry): entry is DocumentEvidenceRunLink =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as DocumentEvidenceRunLink).href === "string" &&
        typeof (entry as DocumentEvidenceRunLink).label === "string"
    );
    return links.length > 0 ? links : null;
  } catch {
    return null;
  }
}

/**
 * The mark a run of citations leaves in the prose: one glyph, a count badge when it stands
 * for several, and the source name as the accessible name rather than visible text. The
 * marker that opens a popover and the inert one a surface without a project draws are the
 * same element, so a preview of Claudia's output carries the mark the transcript carries.
 *
 * `onActivate` is what makes it interactive; without it the chip has no role, no tab stop
 * and nothing to press.
 */
function EvidenceRunMarker({
  anchorKey,
  ariaLabel,
  count,
  icon,
  label,
  onActivate,
  title,
}: {
  anchorKey?: string;
  ariaLabel?: string;
  count: number;
  icon: LucideIcon;
  /** Kept in the chip so copied text keeps the name; the compact style hides it on screen. */
  label?: string;
  onActivate?: (element: HTMLElement) => void;
  title: string;
}) {
  const interactive = onActivate !== undefined;

  return (
    <ReferenceChip
      as="span"
      icon={icon}
      label={label}
      interactive={interactive}
      referenceKind="evidence"
      className="reference-chip--evidence-compact"
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-haspopup={interactive ? "dialog" : undefined}
      aria-label={ariaLabel}
      title={title}
      data-evidence-available={interactive ? "true" : undefined}
      // The provider re-finds this chip by key when the streaming transcript re-renders it
      // under an open popover; the citations behind it live only in React props.
      data-evidence-anchor-key={anchorKey}
      // The count badge is CSS (`content: attr(...)`), so a run of one carries no attribute.
      data-evidence-run-size={count > 1 ? String(count) : undefined}
      onClick={
        interactive
          ? (event) => {
              onActivate(event.currentTarget);
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onActivate(event.currentTarget);
            }
          : undefined
      }
    />
  );
}

/**
 * The compact citation marker for one run of Markdown evidence links: an icon in the prose
 * carrying a count badge when it stands for several citations, and the popover — never a
 * name in the running text, so a sentence backed by four sources reads as a sentence.
 *
 * Degradation matches entity content: a run with nothing resolved yet says "Document
 * evidence", one whose citations are all gone says "Evidence unavailable", and a partly
 * available run shows the marker for the citations that survive.
 */
export function DocumentEvidenceRunReference({
  citations,
}: {
  citations: readonly DocumentEvidenceCitation[];
}) {
  const evidenceInteraction = useOptionalDocumentEvidenceInteraction();
  const evidenceOwner = useDocumentEvidenceOwner();
  const presentations = useDocumentEvidencePresentations(
    citations.map((citation) => citation.reference)
  );

  if (!evidenceInteraction) {
    // Resolving a citation and opening its popover both need a project-scoped provider; the
    // mark itself does not. Without one the run still draws its marker, standing for the
    // labels the author wrote and with nothing behind it to press.
    const labels = citations.map((citation) => citation.fallbackLabel).join(" ");
    return (
      <EvidenceRunMarker
        count={citations.length}
        icon={evidenceDocumentIcon({ documentName: citations[0]?.fallbackLabel })}
        label={labels}
        title={labels}
      />
    );
  }

  const available = citations.flatMap((citation, index) => {
    const presentation = presentations[index];
    return presentation?.status === "available" ? [{ citation, presentation }] : [];
  });

  if (available.length === 0) {
    const loading = presentations.some((presentation) => presentation === undefined);
    return <span>{loading ? DEFAULT_DOCUMENT_EVIDENCE_LABEL : UNAVAILABLE_EVIDENCE_TEXT}</span>;
  }

  const documentName = available[0]?.presentation.documentName ?? DEFAULT_DOCUMENT_EVIDENCE_LABEL;
  const count = available.length;
  // A run's glyph is its first surviving citation's: a run mixing kinds is one marker, and
  // the popover behind it names every source separately anyway.
  const icon = evidenceDocumentIcon({
    fileKind: available[0]?.presentation.fileKind,
    documentName,
  });

  return (
    <EvidenceRunMarker
      anchorKey={available
        .map(({ citation }) => buildEvidenceReferenceKey(citation.reference))
        .join(" ")}
      // The document name is the accessible name of the marker, never visible prose: the
      // popover is where names become readable.
      ariaLabel={
        count > 1
          ? `View document evidence: ${documentName} (+${count - 1} more)`
          : `View document evidence: ${documentName}`
      }
      count={count}
      icon={icon}
      onActivate={(element) => {
        evidenceInteraction.activateEvidenceGroup(
          available.map(({ citation, presentation }) => ({
            reference: citation.reference,
            fallbackLabel: presentation.documentName || citation.fallbackLabel,
          })),
          element,
          { owner: evidenceOwner }
        );
      }}
      title={count > 1 ? `View document evidence (${count})` : "View document evidence"}
    />
  );
}

/**
 * Renders one Markdown `a` node that carries a reserved evidence path, for any surface whose
 * pipeline includes {@link rehypeDocumentEvidenceRuns} — the chat transcript and the ask-user
 * question and option text alike, so a citation looks and behaves the same in both.
 *
 * `enabled` is the surface's document-evidence capability: with it off, and for a
 * reserved-but-invalid link, the run degrades to the labels the model wrote rather than
 * losing the text.
 */
export function renderDocumentEvidenceLink({
  children,
  enabled,
  href,
  node,
}: {
  children: ReactNode;
  enabled: boolean;
  href: string;
  node: unknown;
}): ReactElement {
  // A stamped node stands for its whole run; an unstamped one (an invalid reserved link, or a
  // surface rendering without the transform) is a run of one.
  const runLinks = readDocumentEvidenceRunLinks(node) ?? [
    { href, label: flattenReactNodeText(children).trim() },
  ];
  const labels = runLinks.map(
    (link) => normalizeReferenceLabel(link.label) || DEFAULT_DOCUMENT_EVIDENCE_LABEL
  );
  const citations = enabled
    ? runLinks.flatMap((link, index) => {
        const classified = classifyDocumentEvidenceHref(link.href);
        return classified.kind === "valid"
          ? [
              {
                reference: classified.reference,
                fallbackLabel: labels[index] ?? DEFAULT_DOCUMENT_EVIDENCE_LABEL,
              },
            ]
          : [];
      })
    : [];

  if (citations.length === 0) {
    return <span>{labels.join(" ")}</span>;
  }

  return <DocumentEvidenceRunReference citations={citations} />;
}
